/**
 * Rendering and filtering for the 规则 tab, over the GetRecommendationRules response.
 */
import type { RuleData, RuleSectionData } from '../client/CpaClient';

/** A rendered piece of a rule sentence: plain text, or a parameter value. */
export type RulePart =
  | { kind: 'text'; text: string }
  /** `program`: for a live parameter, the program it is read from. */
  | { kind: 'param'; name: string; value: string | null; live: boolean; program?: string };

/**
 * Splits `rule.text` at its `{name}` placeholders and fills each from its parameter. The service has
 * already filled "program" parameters from live program data; they are marked `live`.
 */
export function renderRule(rule: RuleData): RulePart[] {
  return (rule.text ?? '').split(/(\{[a-zA-Z0-9]+\})/).flatMap((chunk): RulePart[] => {
    const m = /^\{([a-zA-Z0-9]+)\}$/.exec(chunk);
    const param = m ? rule.params?.[m[1]] : undefined;
    if (!m || !param) return chunk ? [{ kind: 'text', text: chunk }] : [];
    const live = param.kind === 'program';
    return [
      {
        kind: 'param',
        name: m[1],
        value: param.value ?? null,
        live,
        ...(live ? { program: param.program ?? '' } : {}),
      },
    ];
  });
}

/** Sections with only the rules that mention `program` (all when `program` is null); empty sections dropped. */
export function filterSections(sections: RuleSectionData[], program: string | null): RuleSectionData[] {
  if (!program) return sections;
  return sections
    .map((s) => ({ ...s, rules: (s.rules ?? []).filter((r) => r.programs?.includes(program)) }))
    .filter((s) => s.rules.length > 0);
}

/** Every program name any rule mentions, in first-mention order. */
export function rulePrograms(sections: RuleSectionData[]): string[] {
  return [...new Set(sections.flatMap((s) => (s.rules ?? []).flatMap((r) => r.programs ?? [])))];
}
